<template>
    <div class="choosing-setup-path-screen">
        <div class="intro">
            <h2>{{ translate("page.category_manager.setup_intro_title") }}</h2>
            <p>{{ translate("page.category_manager.setup_intro_description") }}</p>
        </div>

        <div class="columns">
            <!-- Path 1: Import from Template (Recommended) -->
            <div class="column is-3">
                <div class="box">
                    <div>
                        <span class="is-pulled-right tag is-info">
                            {{ translate("common.recommended") }}
                        </span>
                        <h3>📦 {{ translate("page.category_manager.path_import_template_title") }}</h3>
                        <p>{{ translate("page.category_manager.path_import_template_description") }}</p>
                        <span>⏱️ {{ translate("page.category_manager.path_import_template_time") }}</span>
                    </div>

                    <button class="button is-success is-fullwidth" @click="handlePathSelected('import-template')">
                        <span>{{ translate("page.category_manager.path_import_template_button") }}</span>
                        <span class="icon">
                            <i class="fas fa-arrow-right"></i>
                        </span>
                    </button>
                </div>
            </div>

            <!-- Path 2: Extract from Event Rules -->
            <div class="column is-3">
                <div class="box">
                    <div>
                        <span class="is-pulled-right tag is-warning">
                            {{ translate("common.beta") }}
                        </span>
                        <h3>🤖 {{ translate("page.category_manager.path_extract_rules_title") }}</h3>
                        <p>{{ translate("page.category_manager.path_extract_rules_description") }}</p>
                        <span>⏱️ {{ translate("page.category_manager.path_extract_rules_time") }}</span>
                        <p v-if="!store.isFullOrganizer" class="has-text-warning mt-2">
                            <span class="icon-text">
                                <span class="icon">
                                    <i class="fas fa-exclamation-triangle"></i>
                                </span>
                                <span>{{ translate("page.category_manager.path_extract_rules_permission_required") }}</span>
                            </span>
                        </p>
                    </div>

                    <button
                        class="button is-fullwidth"
                        :disabled="!store.isFullOrganizer"
                        @click="handlePathSelected('extract-from-rules')"
                    >
                        <span>{{ translate("page.category_manager.path_extract_rules_button") }}</span>
                        <span class="icon">
                            <i class="fas fa-arrow-right"></i>
                        </span>
                    </button>
                </div>
            </div>

            <!-- Path 3: Import from Your Previous Event -->
            <div class="column is-3">
                <div class="box">
                    <div>
                        <h3>📋 {{ translate("page.category_manager.path_import_title") }}</h3>
                        <p>{{ translate("page.category_manager.path_import_description") }}</p>
                        <span>⏱️ {{ translate("page.category_manager.path_import_time") }}</span>
                    </div>

                    <button class="button is-fullwidth" @click="handlePathSelected('import-from-previous-event')">
                        <span>{{ translate("page.category_manager.path_import_button") }}</span>
                        <span class="icon">
                            <i class="fas fa-arrow-right"></i>
                        </span>
                    </button>
                </div>
            </div>

            <!-- Path 4: Manual Setup (Advanced) -->
            <div class="column is-3">
                <div class="box">
                    <div>
                        <h3>🔧 {{ translate("page.category_manager.path_manual_title") }}</h3>
                        <p>{{ translate("page.category_manager.path_manual_description") }}</p>
                        <span class="note">{{ translate("page.category_manager.path_manual_note") }}</span>
                    </div>

                    <button class="button is-fullwidth" @click="handlePathSelected('tree-builder')">
                        <span>{{ translate("page.category_manager.path_manual_button") }}</span>
                        <span class="icon">
                            <i class="fas fa-arrow-right"></i>
                        </span>
                    </button>
                </div>
            </div>
        </div>

        <div class="has-text-centered">
            <a :href="urls.helpCenter().categoriesManagement()" target="_blank">
                {{ translate("page.category_manager.learn_more_link") }}
            </a>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { translate } from "../Common/translations";
    import urls from "../Urls/urls";
    import { useCategoriesManagerStore, type PathName } from "./categoriesManagerStore";
    import { handleError } from "../Common/errorHandler";

    const store = useCategoriesManagerStore();

    function handlePathSelected(pathName: PathName) {
        return store.selectPath(pathName).catch(handleError);
    }
</script>

<style scoped></style>
